/**
 * AmbientAudioSources — AUDIO-01 (period-appropriate soundscape) +
 * AUDIO-02 (proximity-based intensity).
 *
 * Plan 05-05 Task 2 contract. Reads `manifest.audioSources[]` and renders
 * one drei <PositionalAudio> per entry. Web Audio API's inverse distance
 * model handles attenuation natively — `distance` prop maps to
 * setRefDistance; rolloffFactor/maxDistance are applied internally by drei
 * when the underlying THREE.PositionalAudio is constructed. No hand-rolled
 * volume math (Don't-Hand-Roll invariant, 05-RESEARCH.md).
 *
 * Autoplay-gate pattern: browsers require a user gesture before an
 * AudioContext can play. The BoardingOverlay click is that gesture
 * (Plan 05-01); it dispatches START, which transitions the machine out of
 * `idle`. So the simple rule "render audio only when navState !== 'idle'"
 * guarantees every PositionalAudio mount happens after the user gesture —
 * no "autoplay was blocked" warning in DevTools.
 *
 * Manifest-driven authoring (PARA-04-style): adding a new audio source is
 * a world.json edit, not a code change. The spawn pose, refDistance,
 * rolloffFactor, maxDistance, loop, url are all per-source manifest fields.
 *
 * AudioListener: NOT mounted here. AudioListenerBinding is the sole owner
 * of the camera listener (mounted once at SceneGraph root). drei's
 * PositionalAudio will find the listener that AudioListenerBinding already
 * attached — no duplicate work.
 */
import { PositionalAudio } from '@react-three/drei';
import { useManifest } from '../content/useManifest';
import { useNavStore } from '../store/useNavStore';

export function AmbientAudioSources() {
  const { manifest } = useManifest();
  const navState = useNavStore((s) => s.navState);

  // Autoplay-gate: no audio until the user clicks BoardingOverlay (which
  // flips navState away from 'idle' via the START event).
  if (navState === 'idle') return <group />;
  if (!manifest) return <group />;

  const sources = manifest.audioSources ?? [];
  if (sources.length === 0) return <group />;

  return (
    <group>
      {sources.map((src) => (
        <PositionalAudio
          key={src.id}
          url={src.url}
          distance={src.refDistance}
          loop={src.loop}
          autoplay
          position={src.position}
        />
      ))}
    </group>
  );
}
